import { z } from 'zod';
import { raiseIfResponseError } from './errors';
import type { AppSettings } from './types';
import { getGlobalPlaygroundAPIBaseURL } from './urls';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export type AnimaSettingsParams = {
  playgroundId?: string;
  playgroundAPIBaseURL?: string;
};

export class AnimaSettings {
  private settings: AppSettings | null = null;

  constructor(private readonly params: AnimaSettingsParams = {}) {}

  async getSettings(): Promise<AppSettings> {
    if (this.settings) {
      return this.settings;
    }

    this.settings = await this.$fetchSettingsWithRetry();
    return this.settings;
  }

  private async $fetchSettingsWithRetry(attempt: number = 1): Promise<AppSettings> {
    try {
      const response = await fetch(`${this.$getPlaygroundAPIBaseURL()}/${this.$getPlaygroundId()}/settings`, {
        method: 'GET',
      });

      await raiseIfResponseError(response);

      const json = await response.json();
      const parsedSettings = settingsSchema.parse(json);

      return {
        requireAuth: parsedSettings.requireAuth,
      };
    } catch (error) {
      if (attempt >= MAX_RETRIES) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      return this.$fetchSettingsWithRetry(attempt + 1);
    }
  }

  $getPlaygroundAPIBaseURL(): string {
    if (this.params.playgroundAPIBaseURL) {
      return this.params.playgroundAPIBaseURL;
    }

    return getGlobalPlaygroundAPIBaseURL();
  }

  $getPlaygroundId(): string {
    if (this.params.playgroundId) {
      return this.params.playgroundId;
    }

    if (typeof window !== 'undefined' && window.__ANIMA_PLAYGROUND_ID__) {
      return window.__ANIMA_PLAYGROUND_ID__;
    }

    throw new Error('Playground ID is not set in window.__ANIMA_PLAYGROUND_ID__');
  }
}

const settingsSchema = z.object({
  requireAuth: z.boolean().optional(),
});
